import { CircleCheck, CircleX, Clock } from 'lucide-react';
import { Card, CardTitle, Spinner } from '@wundo/ui';

export interface UploadItem {
  name: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  /** What went wrong, for the one status that has something to say. */
  error?: string;
}

const STATUS_TEXT: Record<UploadItem['status'], string> = {
  pending: 'Waiting…',
  uploading: 'Uploading…',
  success: 'Complete',
  error: '',
};

function StatusIcon({ status }: { status: UploadItem['status'] }) {
  if (status === 'uploading') return <Spinner className='size-4' />;
  if (status === 'success') {
    return <CircleCheck className='size-4 text-success' aria-hidden='true' />;
  }
  if (status === 'error') {
    return <CircleX className='size-4 text-danger' aria-hidden='true' />;
  }
  return <Clock className='size-4 text-ink-muted' aria-hidden='true' />;
}

/**
 * What the upload is doing, file by file.
 *
 * Files go up one at a time, so this is the only place the progress of the
 * batch is readable: which one is in flight, which are still waiting, and which
 * failed and why. It stands above the table until the batch is done and then
 * clears itself, because the table below is the answer once it is.
 */
export function UploadQueue({ items }: { items: UploadItem[] }) {
  if (items.length === 0) {
    return null;
  }

  const done = items.filter((item) => item.status === 'success').length;

  return (
    <Card as='section' aria-label='Uploading documents' className='mb-3.5'>
      <div className='mb-3.5 flex items-center justify-between gap-3'>
        <CardTitle>Uploading documents</CardTitle>
        <span className='text-xs font-semibold text-ink-muted'>
          {done} / {items.length}
        </span>
      </div>
      <ul className='flex flex-col gap-2'>
        {items.map((item, index) => (
          /* The same file can be picked twice in one batch; only the slot tells them apart. */
          <li key={`${index}-${item.name}`} className='flex items-start gap-2'>
            <span className='mt-0.5 shrink-0'>
              <StatusIcon status={item.status} />
            </span>
            <div className='min-w-0'>
              <p className='truncate text-sm font-semibold text-ink'>{item.name}</p>
              {item.status === 'error' ? (
                <p className='text-xs text-danger'>{item.error}</p>
              ) : (
                <p className='text-xs text-ink-muted'>{STATUS_TEXT[item.status]}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
